import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { API } from 'core/API';
import { INITIAL_PAGINATION } from 'core/constants';

import { PaginationRequest, SearchPostsRequest, SearchPostSort, SearchPostsResponse } from '@joc/api-gateway';
import { POST_POSSIBLE_SORT, SortDirection } from '@joc/api-gateway/lib/api-client';
import { useState } from 'react';

export const SUPER_ADMIN_POSTS_QUERY_KEY = 'superAdminPosts';

type UseAdminPostsSearchParams = {
	search?: string;
	sortBy?: POST_POSSIBLE_SORT;
	sortDirection?: SortDirection;
	pageSize?: number;
};

export const useAdminPostsSearch = ({
	search,
	sortBy,
	sortDirection,
	pageSize = INITIAL_PAGINATION.take,
}: UseAdminPostsSearchParams) =>
	useInfiniteQuery<SearchPostsResponse, unknown>({
		queryKey: [SUPER_ADMIN_POSTS_QUERY_KEY, search || '', sortBy || '', sortDirection || '', pageSize],
		initialPageParam: 0,
		retry: false,
		refetchOnWindowFocus: false,
		queryFn: async ({ pageParam = 0 }) => {
			const body = SearchPostsRequest.fromJS({
				pagination: PaginationRequest.fromJS({
					skip: pageParam as number,
					take: pageSize,
				}),
				...(search ? { search } : {}),
				...(sortBy && sortDirection
					? {
							sort: SearchPostSort.fromJS({
								sortBy,
								sortDir: sortDirection,
							}),
					  }
					: {}),
			});

			return API.searchPostsByAdmin(body);
		},
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.records.length < pageSize ? undefined : allPages.length * pageSize;
		},
	});

export const useAdminPostsCount = () => {
	const queryClient = useQueryClient();
	const [totalItems, setTotalItems] = useState(0);

	queryClient.getQueryCache().subscribe(cache => {
		if (cache?.query?.queryKey?.includes(SUPER_ADMIN_POSTS_QUERY_KEY))
			setTimeout(() => setTotalItems(cache?.query?.state?.data?.pages?.[0]?.total ?? 0), 300);
	});

	return totalItems || 0;
};
